import { ColDef, createGrid, GridOptions } from 'ag-grid-community';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { every, isEmpty, map, reduce } from 'lodash';

import { IInputsResponse } from './index';

// Excel sheet names are capped at 31 characters and cannot contain : \ / ? * [ ]
export const sheetNameFor = (inputName: string): string =>
  inputName.replace(/[:\\/?*[\]]/g, '_').slice(0, 31);

// selectedPaths are leaf paths from the tree, e.g. "Country>France"; groups them per DimensionHeader name
const buildCheckedValuesByHeader = (
  selectedPaths: string[]
): Record<string, Set<string>> =>
  reduce(
    selectedPaths,
    (byHeader, path) => {
      // Split on the first '>' only - dimension values themselves may contain one
      const separatorIndex = path.indexOf('>');
      if (separatorIndex === -1) {
        return byHeader;
      }
      const headerName = path.slice(0, separatorIndex);
      const value = path.slice(separatorIndex + 1).trim();
      if (!byHeader[headerName]) {
        byHeader[headerName] = new Set();
      }
      byHeader[headerName].add(value);
      return byHeader;
    },
    {} as Record<string, Set<string>>
  );

// Keeps only the Series rows whose dimension values were checked; a header with nothing checked isn't filtered on.
// Inputs left with no rows are kept (with empty Series) so the grid still shows their tab.
export const filterSeriesBySelection = (
  inputs: IInputsResponse[],
  selectedPaths: string[]
): IInputsResponse[] => {
  const checkedValuesByHeader = buildCheckedValuesByHeader(selectedPaths);

  return inputs.map((input) => ({
    ...input,
    Series: input.Series.filter((series) =>
      every(input.DimensionHeaders, (header) => {
        const checkedValues = checkedValuesByHeader[header.Name];
        return (
          !checkedValues ||
          checkedValues.has(`${(series as any)[header.Field] ?? ''}`.trim())
        );
      })
    ),
  }));
};

// Same as filterSeriesBySelection, but drops inputs left with no rows (nothing to put in their sheet)
export const filterInputsBySelection = (
  inputs: IInputsResponse[],
  selectedPaths: string[]
): IInputsResponse[] =>
  filterSeriesBySelection(inputs, selectedPaths).filter(
    (input) => !isEmpty(input.Series)
  );

// Mirrors InputTabPanel's getColumns: DimensionHeaders pinned left, then ValueHeaders. Group is
// exported too (even though it isn't shown in the editor grid) because it's often the only thing
// that distinguishes two rows with otherwise identical dimension values - dropping it here would
// make re-uploaded rows impossible to match back to the right Series entry.
const buildColumnDefs = (input: IInputsResponse): ColDef[] => [
  // { headerName: 'Group', field: 'Group' },
  ...map(input.DimensionHeaders, (header) => ({
    headerName: header.Name,
    field: header.Name,
  })),
  ...map(input.ValueHeaders, (header) => ({
    headerName: header,
    field: header,
  })),
];

// Mirrors InputTabPanel's rowData reduce: one row per Series entry
const buildRowData = (input: IInputsResponse): Record<string, unknown>[] =>
  map(input.Series, (series) =>
    reduce(
      input.ValueHeaders,
      (row, header, index) => ({
        ...row,
        [header]: (series as any).Values[index],
      }),
      reduce(
        input.DimensionHeaders,
        (row, header) => ({
          ...row,
          [header.Name]: (series as any)[header.Field],
        }),
        { Group: (series as any).Group } as Record<string, unknown>
      )
    )
  );

// Identifies which client/model/instance/scenario (the URL's client, model, instance, scenario
// params) a template was downloaded from, so an upload can be rejected when it targets another one.
export interface ITemplateScope {
  client?: number;
  model?: number;
  instance?: number;
  scenario?: number;
}

export const TEMPLATE_INFO_SHEET = 'Template Info';
export const TEMPLATE_SCOPE_KEYS = [
  'client',
  'model',
  'instance',
  'scenario',
] as const;

// Builds one Excel sheet per input via a detached grid instance, then merges them into a single workbook
export const exportInputsAsExcel = async (
  inputs: IInputsResponse[],
  scope: ITemplateScope,
  fileName?: string
): Promise<void> => {
  if (isEmpty(inputs)) {
    return;
  }

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '-10000px';
  container.style.left = '-10000px';
  container.style.width = '800px';
  container.style.height = '600px';
  document.body.appendChild(container);

  const sheets = map(inputs, (input) => {
    const gridOptions: GridOptions = {
      columnDefs: buildColumnDefs(input),
      rowData: buildRowData(input),
    };
    const api = createGrid(container, gridOptions);
    const sheet = api.getSheetDataForExcel({
      sheetName: sheetNameFor(input.Name),
    }) as string;
    api.destroy();
    return sheet;
  });

  const infoApi = createGrid(container, {
    columnDefs: [
      { headerName: 'Key', field: 'key' },
      { headerName: 'Value', field: 'value' },
    ],
    rowData: map([...TEMPLATE_SCOPE_KEYS], (key) => ({
      key,
      value: scope[key] ?? '',
    })),
  });
  sheets.push(
    infoApi.getSheetDataForExcel({ sheetName: TEMPLATE_INFO_SHEET }) as string
  );
  infoApi.destroy();

  const exportApi = createGrid(container, {});
  const blob = exportApi.getMultipleSheetsAsExcel({
    data: sheets,
    author: 'Trinity LifeSciences',
  });
  exportApi.destroy();
  document.body.removeChild(container);
  if (!blob) {
    throw new Error('Could not build the template workbook');
  }

  // AG Grid can't hide a sheet, so re-open its workbook and mark the info sheet "very hidden"
  // (not listed in Excel's Unhide dialog); parseTemplateScope still reads it by name.
  const workbook = XLSX.read(await blob.arrayBuffer(), { type: 'array' });
  workbook.Workbook = {
    ...workbook.Workbook,
    Sheets: map(workbook.SheetNames, (name) => ({
      name,
      Hidden: name === TEMPLATE_INFO_SHEET ? 2 : 0,
    })),
  };
  XLSX.writeFile(
    workbook,
    `${fileName ? fileName.replace(/[\\/:*?"<>|]/g, '_') : 'Template'}-${format(new Date(), 'MM-dd-yyyy')}.xlsx`
  );
};
